/** A4 PDF 导出：三种用途（直接制作模板 / 步骤说明页 / 独立描剪底样）。
 *
 * 设计要求（规格书 7）：
 * - 真正的 PDF 文件，不是"调用浏览器打印"；
 * - 210 × 297 mm，预留 10 mm 边距，全程毫米坐标；
 * - 剪掉区域浅灰填充；折线与剪切线不同线型，并附图例；
 * - 精确 50 mm 校验条；
 * - 中文不乱码：中文标签以矢量无关的位图文字块嵌入（见 TextBlockRenderer），
 *   线稿保持矢量，物理尺寸不受影响。
 */
import { PDFDocument, StandardFonts, rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import type { Project, Vec } from '../domain/types';
import { getFold } from '../domain/folds/definitions';
import { computeUnfold, foldedView, paperGeometry } from '../domain/folds/geometry';
import type { MultiPoly, Ring } from '../domain/geometry/boolean';
import { boundsOf, polygonToPath, type Bounds } from '../domain/geometry/polygon';
import { A4, PRINT_MARGIN_MM, SCALE_BAR_MM } from '../domain/units';

export const MM_TO_PT = 72 / 25.4;
export const PAGE_WIDTH_PT = A4.widthMm * MM_TO_PT;
export const PAGE_HEIGHT_PT = A4.heightMm * MM_TO_PT;

/** 50 mm 校验条在页面上的位置（mm，左上角原点）。 */
export const SCALE_BAR = {
  xMm: PRINT_MARGIN_MM,
  yMm: A4.heightMm - PRINT_MARGIN_MM - 6,
  lengthMm: SCALE_BAR_MM,
} as const;

export interface RasterText {
  png: Uint8Array;
  widthPt: number;
  heightPt: number;
}

export interface TextBlockRenderer {
  render(text: string, fontPt: number, weight?: 'normal' | 'bold'): Promise<RasterText | null>;
}

type Align = 'left' | 'center' | 'right';

interface Box {
  xMm: number;
  yMm: number;
  wMm: number;
  hMm: number;
}

const CONTENT: Box = {
  xMm: PRINT_MARGIN_MM,
  yMm: 34,
  wMm: A4.widthMm - PRINT_MARGIN_MM * 2,
  hMm: 196,
};

interface Fit {
  scale: number;
  ox: number;
  oy: number;
  map: (p: Vec) => { x: number; y: number };
}

function fitToBox(b: Bounds, box: Box, allowUpscale = false): Fit {
  const w = Math.max(1e-6, b.maxX - b.minX);
  const h = Math.max(1e-6, b.maxY - b.minY);
  const raw = Math.min(box.wMm / w, box.hMm / h);
  const scale = allowUpscale ? raw : Math.min(raw, 1);
  const ox = box.xMm + (box.wMm - w * scale) / 2;
  const oy = box.yMm + (box.hMm - h * scale) / 2;
  return {
    scale,
    ox,
    oy,
    map: (p) => ({ x: (p.x - b.minX) * scale + ox, y: (b.maxY - p.y) * scale + oy }),
  };
}

function pathForRings(rings: Ring[], fit: Fit): string {
  let d = '';
  for (const ring of rings) {
    if (ring.length < 3) continue;
    const p0 = fit.map(ring[0]);
    d += `M${p0.x.toFixed(3)} ${p0.y.toFixed(3)}`;
    for (let i = 1; i < ring.length; i++) {
      const p = fit.map(ring[i]);
      d += `L${p.x.toFixed(3)} ${p.y.toFixed(3)}`;
    }
    d += 'Z';
  }
  return d;
}

function pathForMulti(mp: MultiPoly, fit: Fit): string {
  let d = '';
  for (const rings of mp) d += pathForRings(rings, fit);
  return d;
}

/** drawSvgPath 的锚点：路径数据以 mm 为单位、y 向下。 */
function anchor(fit: Fit) {
  return { x: fit.ox * MM_TO_PT, y: PAGE_HEIGHT_PT - fit.oy * MM_TO_PT, scale: fit.scale * MM_TO_PT };
}

const GRAY_FILL = rgb(0.86, 0.86, 0.86);
const GRAY_LINE = rgb(0.35, 0.35, 0.35);
const LIGHT_LINE = rgb(0.62, 0.62, 0.62);
const BLACK = rgb(0, 0, 0);

async function placeText(
  page: PDFPage,
  doc: PDFDocument,
  helvetica: PDFFont,
  renderer: TextBlockRenderer | undefined,
  text: string,
  xMm: number,
  yTopMm: number,
  fontPt: number,
  align: Align = 'left',
  font = 'normal' as 'normal' | 'bold',
): Promise<void> {
  if (renderer) {
    const raster = await renderer.render(text, fontPt, font);
    if (raster) {
      const img = await doc.embedPng(raster.png);
      const xPt = xMm * MM_TO_PT - (align === 'center' ? raster.widthPt / 2 : align === 'right' ? raster.widthPt : 0);
      const yPt = PAGE_HEIGHT_PT - yTopMm * MM_TO_PT - raster.heightPt;
      page.drawImage(img, { x: xPt, y: yPt, width: raster.widthPt, height: raster.heightPt });
      return;
    }
  }
  // 无渲染器时的 ASCII 兜底，保证测试与非中文环境仍可生成
  const ascii = text.replace(/[^\x20-\x7E]/g, '');
  if (!ascii.trim()) return;
  const width = helvetica.widthOfTextAtSize(ascii, fontPt);
  const xPt = xMm * MM_TO_PT - (align === 'center' ? width / 2 : align === 'right' ? width : 0);
  page.drawText(ascii, {
    x: xPt,
    y: PAGE_HEIGHT_PT - yTopMm * MM_TO_PT - fontPt,
    size: fontPt,
    font: helvetica,
    color: BLACK,
  });
}

function drawRule(page: PDFPage, x1: number, y1: number, x2: number, y2: number, thickness = 0.5, color = LIGHT_LINE) {
  page.drawLine({
    start: { x: x1 * MM_TO_PT, y: PAGE_HEIGHT_PT - y1 * MM_TO_PT },
    end: { x: x2 * MM_TO_PT, y: PAGE_HEIGHT_PT - y2 * MM_TO_PT },
    thickness,
    color,
  });
}

/** 50 mm 校验条：两端带端帽，标注 0 与 50 mm。 */
async function drawScaleBar(
  page: PDFPage,
  doc: PDFDocument,
  helvetica: PDFFont,
  renderer: TextBlockRenderer | undefined,
): Promise<void> {
  const y = SCALE_BAR.yMm;
  const x0 = SCALE_BAR.xMm;
  const x1 = x0 + SCALE_BAR.lengthMm;
  drawRule(page, x0, y, x1, y, 1.1, BLACK);
  drawRule(page, x0, y - 1.6, x0, y + 1.6, 1.1, BLACK);
  drawRule(page, x1, y - 1.6, x1, y + 1.6, 1.1, BLACK);
  await placeText(page, doc, helvetica, renderer, '50 mm 校验条（实际长度 50.0 mm）', x0, y - 6, 8);
  await placeText(page, doc, helvetica, renderer, '0', x0, y - 3.4, 6, 'left');
  await placeText(page, doc, helvetica, renderer, '50', x1, y - 3.4, 6, 'right');
}

/** 线型图例：灰阶可辨，不依赖颜色。 */
async function drawLegend(
  page: PDFPage,
  doc: PDFDocument,
  helvetica: PDFFont,
  renderer: TextBlockRenderer | undefined,
): Promise<void> {
  const x = PRINT_MARGIN_MM;
  const y = A4.heightMm - PRINT_MARGIN_MM - 26;
  const rowH = 5;
  await placeText(page, doc, helvetica, renderer, '图例', x, y - 3, 8, 'left', 'bold');

  // 剪切区域
  page.drawRectangle({
    x: x * MM_TO_PT,
    y: PAGE_HEIGHT_PT - (y + rowH) * MM_TO_PT,
    width: 8 * MM_TO_PT,
    height: 3.4 * MM_TO_PT,
    color: GRAY_FILL,
    borderColor: GRAY_LINE,
    borderWidth: 0.7,
  });
  await placeText(page, doc, helvetica, renderer, '剪切区域（剪掉）', x + 10, y + 1.2, 7.5);

  // 谷折
  drawRule(page, x, y + rowH + 3, x + 8, y + rowH + 3, 0.9, BLACK);
  await placeText(page, doc, helvetica, renderer, '谷折线', x + 10, y + rowH + 1.2, 7.5);

  // 山折
  page.drawLine({
    start: { x: x * MM_TO_PT, y: PAGE_HEIGHT_PT - (y + rowH * 2 + 6) * MM_TO_PT },
    end: { x: (x + 8) * MM_TO_PT, y: PAGE_HEIGHT_PT - (y + rowH * 2 + 6) * MM_TO_PT },
    thickness: 0.9,
    color: BLACK,
    dashArray: [4, 1.5, 1, 1.5],
  });
  await placeText(page, doc, helvetica, renderer, '山折线', x + 10, y + rowH * 2 + 4.2, 7.5);
}

async function drawHeader(
  page: PDFPage,
  doc: PDFDocument,
  helvetica: PDFFont,
  renderer: TextBlockRenderer | undefined,
  title: string,
  subtitle: string,
): Promise<void> {
  await placeText(page, doc, helvetica, renderer, title, PRINT_MARGIN_MM, 16, 15, 'left', 'bold');
  await placeText(page, doc, helvetica, renderer, subtitle, PRINT_MARGIN_MM, 23, 8.5);
  drawRule(page, PRINT_MARGIN_MM, 28, A4.widthMm - PRINT_MARGIN_MM, 28, 0.8, LIGHT_LINE);
}

async function drawFooter(
  page: PDFPage,
  doc: PDFDocument,
  helvetica: PDFFont,
  renderer: TextBlockRenderer | undefined,
  text: string,
): Promise<void> {
  drawRule(page, PRINT_MARGIN_MM, A4.heightMm - 20, A4.widthMm - PRINT_MARGIN_MM, A4.heightMm - 20, 0.4, LIGHT_LINE);
  await placeText(page, doc, helvetica, renderer, text, PRINT_MARGIN_MM, A4.heightMm - 17, 7);
  await placeText(
    page,
    doc,
    helvetica,
    renderer,
    '请按 100% / 实际大小打印，并关闭"适应页面"。',
    A4.widthMm - PRINT_MARGIN_MM,
    A4.heightMm - 17,
    7,
    'right',
  );
}

function sizeLabel(project: Project): string {
  const v = project.finishedSize.value;
  if (project.finishedSize.kind === 'diameter') return `外接圆直径 ${v[0].toFixed(1)} mm`;
  return `展开后 ${v[0].toFixed(0)} × ${v[1].toFixed(0)} mm`;
}

export interface PdfBuildOptions {
  textRenderer?: TextBlockRenderer;
}

export async function buildProjectPdf(project: Project, options: PdfBuildOptions = {}): Promise<Uint8Array> {
  const fold = getFold(project.foldId);
  const renderer = options.textRenderer;
  const doc = await PDFDocument.create();
  const helvetica = await doc.embedFont(StandardFonts.Helvetica);
  const sizeMm = { width: PAGE_WIDTH_PT, height: PAGE_HEIGHT_PT };

  const geo = paperGeometry(project.foldId, project.finishedSize);
  const folded = foldedView(project.foldId, project.finishedSize, project.cuts);
  const unfolded = computeUnfold(project.foldId, project.finishedSize, project.cuts);
  const header = `${fold.name}（${fold.layers} 层）— ${sizeLabel(project)}`;
  const footer = `A4 剪纸与窗花生成器 · ${fold.name} · 层数 ${fold.layers} · 难度 ${fold.difficulty}/5 · 建议 ${fold.recommendedGsm} g/m²`;

  // ---------- 第 1 页：直接制作模板
  const p1 = doc.addPage([sizeMm.width, sizeMm.height]);
  await drawHeader(p1, doc, helvetica, renderer, '直接制作模板', header);
  {
    const b = boundsOf(geo.editable);
    const fit = fitToBox(b, CONTENT);
    const a = anchor(fit);
    // 折后纸张
    p1.drawSvgPath(polygonToPath(geo.editable), {
      x: a.x,
      y: a.y,
      scale: a.scale,
      color: rgb(1, 1, 1),
      borderColor: GRAY_LINE,
      borderWidth: 0.8,
    });
    // 剪口
    if (folded.cutRegion.length) {
      p1.drawSvgPath(pathForMulti(folded.cutRegion, fit), {
        x: a.x,
        y: a.y,
        scale: a.scale,
        color: GRAY_FILL,
        borderColor: GRAY_LINE,
        borderWidth: 0.7,
      });
    }
    // 折线（虚线，谷折）
    for (const deg of geo.creaseAnglesDeg) {
      const rad = (deg * Math.PI) / 180;
      const r = Math.max(b.maxX - b.minX, b.maxY - b.minY);
      const p1mm = fit.map({ x: -Math.cos(rad) * r, y: -Math.sin(rad) * r });
      const p2mm = fit.map({ x: Math.cos(rad) * r, y: Math.sin(rad) * r });
      p1.drawLine({
        start: { x: p1mm.x * MM_TO_PT, y: PAGE_HEIGHT_PT - p1mm.y * MM_TO_PT },
        end: { x: p2mm.x * MM_TO_PT, y: PAGE_HEIGHT_PT - p2mm.y * MM_TO_PT },
        thickness: 0.5,
        color: LIGHT_LINE,
        dashArray: [3, 2],
      });
    }
    await placeText(p1, doc, helvetica, renderer, '折后纸（画灰处剪掉）', b.minX === b.minX ? CONTENT.xMm : CONTENT.xMm, 30, 8);
  }
  await drawLegend(p1, doc, helvetica, renderer);
  await drawScaleBar(p1, doc, helvetica, renderer);
  await drawFooter(p1, doc, helvetica, renderer, footer);

  // ---------- 第 2 页：步骤说明
  const p2 = doc.addPage([sizeMm.width, sizeMm.height]);
  await drawHeader(p2, doc, helvetica, renderer, '折叠步骤', `${fold.name} · ${fold.layers} 层 · ${fold.paperNote}`);
  {
    let y = 40;
    for (const s of fold.steps) {
      await placeText(p2, doc, helvetica, renderer, `第 ${s.index} 步`, PRINT_MARGIN_MM, y, 10, 'left', 'bold');
      await placeText(p2, doc, helvetica, renderer, s.text, PRINT_MARGIN_MM + 22, y, 10);
      await placeText(p2, doc, helvetica, renderer, `提示：${s.hint}`, PRINT_MARGIN_MM + 22, y + 6, 8);
      y += 18;
    }
    // 折后形状示意
    const b = boundsOf(geo.editable);
    const box: Box = { xMm: PRINT_MARGIN_MM, yMm: 110, wMm: 90, hMm: 70 };
    const fit = fitToBox(b, box);
    const a = anchor(fit);
    p2.drawSvgPath(polygonToPath(geo.editable), {
      x: a.x,
      y: a.y,
      scale: a.scale,
      color: rgb(1, 1, 1),
      borderColor: GRAY_LINE,
      borderWidth: 0.8,
    });
    if (folded.cutRegion.length) {
      p2.drawSvgPath(pathForMulti(folded.cutRegion, fit), {
        x: a.x,
        y: a.y,
        scale: a.scale,
        color: GRAY_FILL,
        borderColor: GRAY_LINE,
        borderWidth: 0.7,
      });
    }
    await placeText(p2, doc, helvetica, renderer, '折后形状与剪口位置（示意）', PRINT_MARGIN_MM, 182, 8);

    // 展开成品预览
    const ub = boundsOf(geo.paper);
    const box2: Box = { xMm: 110, yMm: 110, wMm: 90, hMm: 70 };
    const fit2 = fitToBox(ub, box2);
    const a2 = anchor(fit2);
    p2.drawSvgPath(
      polygonToPath(geo.paper) + (unfolded.holes.length ? pathForMulti(unfolded.holes, fit2) : ''),
      { x: a2.x, y: a2.y, scale: a2.scale, color: rgb(0.92, 0.9, 0.88), borderColor: GRAY_LINE, borderWidth: 0.6 },
    );
    await placeText(p2, doc, helvetica, renderer, '展开成品预览', 110, 182, 8);

    await placeText(
      p2,
      doc,
      helvetica,
      renderer,
      `制作提示：${fold.paperNote} 建议纸厚 ${fold.recommendedGsm} g/m²，最小连接宽度约 ${fold.minBridgeMm} mm。`,
      PRINT_MARGIN_MM,
      192,
      8.5,
    );
  }
  await drawScaleBar(p2, doc, helvetica, renderer);
  await drawFooter(p2, doc, helvetica, renderer, footer);

  // ---------- 第 3 页：独立描剪底样
  const p3 = doc.addPage([sizeMm.width, sizeMm.height]);
  await drawHeader(p3, doc, helvetica, renderer, '独立描剪底样', `对齐后描画剪口 · ${fold.name} · 方向：折线向上为纸的上边`);
  {
    const b = boundsOf(geo.editable);
    const fit = fitToBox(b, { xMm: PRINT_MARGIN_MM, yMm: 36, wMm: A4.widthMm - PRINT_MARGIN_MM * 2, hMm: 190 });
    const a = anchor(fit);
    p3.drawSvgPath(polygonToPath(geo.editable), {
      x: a.x,
      y: a.y,
      scale: a.scale,
      color: rgb(1, 1, 1),
      borderColor: BLACK,
      borderWidth: 1,
    });
    if (folded.cutRegion.length) {
      p3.drawSvgPath(pathForMulti(folded.cutRegion, fit), {
        x: a.x,
        y: a.y,
        scale: a.scale,
        color: GRAY_FILL,
        borderColor: BLACK,
        borderWidth: 0.9,
      });
    }
    // 方向角标：非对称 L 形，折后可自检正反面
    const corner = fit.map({ x: b.maxX, y: b.maxY });
    p3.drawLine({
      start: { x: (corner.x - 8) * MM_TO_PT, y: PAGE_HEIGHT_PT - corner.y * MM_TO_PT },
      end: { x: (corner.x - 8) * MM_TO_PT, y: PAGE_HEIGHT_PT - (corner.y + 3) * MM_TO_PT },
      thickness: 1.2,
      color: BLACK,
    });
    p3.drawLine({
      start: { x: (corner.x - 8) * MM_TO_PT, y: PAGE_HEIGHT_PT - (corner.y + 3) * MM_TO_PT },
      end: { x: (corner.x - 2) * MM_TO_PT, y: PAGE_HEIGHT_PT - (corner.y + 3) * MM_TO_PT },
      thickness: 1.2,
      color: BLACK,
    });
    await placeText(
      p3,
      doc,
      helvetica,
      renderer,
      '折叠完成后，右上角的方向角标应朝外；若角标在内侧，说明折反了面。',
      PRINT_MARGIN_MM,
      A4.heightMm - 34,
      8,
    );
  }
  await drawScaleBar(p3, doc, helvetica, renderer);
  await drawFooter(p3, doc, helvetica, renderer, footer);

  return doc.save();
}

/** 供测试与文档使用的页面尺寸断言值。 */
export function pdfPageSizeMm(): { widthMm: number; heightMm: number } {
  return {
    widthMm: PAGE_WIDTH_PT / MM_TO_PT,
    heightMm: PAGE_HEIGHT_PT / MM_TO_PT,
  };
}